/**
 * EditVenueForm.tsx
 *
 * Full inline edit form for a venue and its schedules.
 * Saves directly to Supabase — no moderation queue.
 * Admin only — accessed via AdminPage venue cards.
 */

import React, { useState, useRef } from 'react'
import { PlacesSearch, type PlaceResult } from './PlacesSearch'
import { supabase } from '../lib/supabase'
import type { Venue, HappyHourSchedule, DayOfWeek, DealType } from '../types'
import { DAYS_OF_WEEK } from '../types'
import { fmtTime } from '../utils/filters'
import {
  getVenueEvents, createVenueEvent, updateVenueEvent, deleteVenueEvent,
} from '../services/venueEventService'
import type { VenueEvent } from '../services/venueEventService'

type DealValueType = 'price' | 'amount_off' | 'percent_off'

interface DealItem {
  type: DealType
  description: string
  value: number | undefined
  valueType: DealValueType
}

const DEAL_TYPE_OPTIONS: { value: DealType; label: string; emoji: string }[] = [
  { value: 'beer',     label: 'Beer',     emoji: '🍺' },
  { value: 'cocktail', label: 'Cocktail', emoji: '🍸' },
  { value: 'liquor',   label: 'Liquor',   emoji: '🥃' },
  { value: 'wine',     label: 'Wine',     emoji: '🍷' },
  { value: 'food',     label: 'Food',     emoji: '🍔' },
  { value: 'general',  label: 'General',  emoji: '🏷️' },
]

const VALUE_TYPE_OPTIONS: { value: DealValueType; label: string }[] = [
  { value: 'price',       label: 'is' },
  { value: 'amount_off',  label: '$ off' },
  { value: 'percent_off', label: '% off' },
]

function Field({ label, required, hint, children }: {
  label: string; required?: boolean; hint?: string; children: React.ReactNode
}) {
  return (
    <div className="ef-field">
      <label className="ef-label">
        {label}
        {required && <span className="ef-required"> *</span>}
      </label>
      {hint && <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  )
}

function emptyDeal(): DealItem {
  return { type: 'beer', description: '', value: undefined, valueType: 'price' }
}

// Reads a schedule's stored deals into editable state. Handles both the
// new shape (value/value_type) and legacy rows that only ever had
// price — those default to valueType 'price', preserving exactly how
// they render today. No migration needed: value_type simply didn't
// exist as a key on old jsonb rows, so it's undefined, not wrong.
function dealsFromSchedule(schedule: HappyHourSchedule): DealItem[] {
  if (schedule.deals && schedule.deals.length > 0) {
    return schedule.deals.map((d: any) => {
      const vt: DealValueType = (d.value_type === 'amount_off' || d.value_type === 'percent_off')
        ? d.value_type : 'price'
      const raw = vt === 'price' ? (d.price ?? d.value) : d.value
      return {
        type: d.type ?? 'general',
        description: d.description ?? '',
        value: raw ?? undefined,
        valueType: vt,
      }
    })
  }
  return [emptyDeal()]
}

// Converts editable state back to the DB shape. `price` is kept
// populated ONLY for valueType 'price' — this is what scoring.ts and
// the cheapest-drinks sort read, and a $2-off deal is not a $2 price,
// so it must not populate that field.
function dealToDbShape(d: DealItem) {
  return {
    type: d.type,
    description: d.description,
    price: d.valueType === 'price' ? (d.value ?? null) : null,
    value: d.value ?? null,
    value_type: d.valueType,
  }
}

// Builds the human-readable phrase for one deal, matching its shape.
function fmtDealPhrase(d: DealItem): string {
  if (d.value == null) return d.description
  if (d.valueType === 'amount_off')  return `$${d.value} off ${d.description}`.trim()
  if (d.valueType === 'percent_off') return `${d.value}% off ${d.description}`.trim()
  return `${d.description} $${d.value}`.trim()
}

// ─────────────────────────────────────────────
// DEAL ROW
// ─────────────────────────────────────────────

function DealRow({
  deal, onChange, onRemove, canRemove,
}: {
  deal: DealItem
  onChange: (d: DealItem) => void
  onRemove: () => void
  canRemove: boolean
}) {
  return (
    <div style={{
      marginBottom: 8,
      background: '#FFFBF5', border: '1px solid #F0E8D8', borderRadius: 10, padding: '10px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <select
          value={deal.type}
          onChange={e => onChange({ ...deal, type: e.target.value as DealType })}
          style={{
            flexShrink: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid #E0DDD8',
            fontSize: 13, fontFamily: 'inherit', background: '#fff', width: 110,
          }}
        >
          {DEAL_TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.emoji} {o.label}</option>
          ))}
        </select>
        <input
          value={deal.description}
          onChange={e => onChange({ ...deal, description: e.target.value })}
          placeholder="e.g. draft beer"
          style={{
            flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8,
            border: '1px solid #E0DDD8', fontSize: 13, fontFamily: 'inherit', background: '#fff',
          }}
        />
        {canRemove && (
          <button
            onClick={onRemove}
            type="button"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 20, flexShrink: 0, padding: '0 4px', lineHeight: 1 }}
          >✕</button>
        )}
      </div>
      {/* Price / $ off / % off — three different deal shapes. "$2 Off
          Drafts" is a discount, not a $2 price; keeping these separate
          is what stopped cards from rendering "$2 Off Drafts $2".
          Labeled "Price" so the dropdown reads with context instead of
          floating as a bare, unexplained "is". */}
      <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Price</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={deal.valueType}
          onChange={e => onChange({ ...deal, valueType: e.target.value as DealValueType })}
          style={{
            fontSize: 13, padding: '8px 8px', borderRadius: 8, border: '1px solid #E0DDD8',
            background: '#fff', flexShrink: 0, width: 84,
          }}
        >
          {VALUE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div style={{ position: 'relative', flex: 1 }}>
          {deal.valueType !== 'percent_off' && (
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 13 }}>$</span>
          )}
          <input
            type="number"
            min="0"
            step="0.5"
            value={deal.value ?? ''}
            onChange={e => onChange({ ...deal, value: e.target.value ? parseFloat(e.target.value) : undefined })}
            placeholder="—"
            style={{
              width: '100%',
              padding: deal.valueType === 'percent_off' ? '8px 24px 8px 10px' : '8px 10px 8px 22px',
              borderRadius: 8,
              border: '1px solid #E0DDD8', fontSize: 13, fontFamily: 'inherit',
              boxSizing: 'border-box' as const,
            }}
          />
          {deal.valueType === 'percent_off' && (
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 13 }}>%</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// SCHEDULE EDITOR
// ─────────────────────────────────────────────

function ScheduleEditor({
  schedule, index, onSave, onDelete, onCancel,
}: {
  schedule: HappyHourSchedule
  index: number
  onSave: (updates: Partial<HappyHourSchedule>) => Promise<void>
  onDelete: () => Promise<void>
  onCancel: () => void
}) {
  const [days, setDays]         = useState<DayOfWeek[]>(schedule.days as DayOfWeek[])
  const [isAllDay, setIsAllDay] = useState(schedule.is_all_day ?? false)
  const [startTime, setStart]   = useState(schedule.start_time)
  const [endTime, setEnd]       = useState(schedule.end_time)
  const [deals, setDeals]       = useState<DealItem[]>(dealsFromSchedule(schedule))
  const [dealText, setDealText] = useState(schedule.deal_text)
  const [saving, setSaving]     = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError]       = useState<string | null>(null)

  function toggleDay(day: DayOfWeek) {
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  function updateDeal(i: number, d: DealItem) {
    setDeals(prev => prev.map((item, idx) => idx === i ? d : item))
  }

  function removeDeal(i: number) {
    setDeals(prev => prev.filter((_, idx) => idx !== i))
  }

  async function handleSave() {
    if (days.length === 0) { setError('Select at least one day'); return }
    const validDeals = deals.filter(d => d.description.trim())
    setSaving(true)
    setError(null)
    try {
      await onSave({
        days, start_time: startTime, end_time: endTime, is_all_day: isAllDay,
        deals: validDeals.map(dealToDbShape) as any,
        deal_text: dealText.trim() || validDeals.map(fmtDealPhrase).join(', '),
      })
    } catch (e: any) { setError(e.message) }
    setSaving(false)
  }

  async function handleDelete() {
    if (!window.confirm('Delete this schedule? This cannot be undone.')) return
    setDeleting(true)
    await onDelete()
    setDeleting(false)
  }

  return (
    <div style={{ background: '#F8F6F1', borderRadius: 12, padding: '16px', border: '1px solid #E0DDD8', marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#1A1612', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>
        Schedule {index + 1}
      </div>
      {error && <div style={{ background: '#fee2e2', color: '#c0392b', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
      <Field label="Days" required>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {DAYS_OF_WEEK.map(d => (
            <button key={d} type="button" onClick={() => toggleDay(d)} style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: '2px solid',
              background: days.includes(d) ? '#1A1612' : '#fff', color: days.includes(d) ? '#fff' : '#888',
              borderColor: days.includes(d) ? '#1A1612' : '#E0DDD8', transition: 'all .15s',
            }}>{d}</button>
          ))}
        </div>
      </Field>
      <div onClick={() => setIsAllDay(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 14, userSelect: 'none' }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid', borderColor: isAllDay ? '#E85D1A' : '#E0DDD8', background: isAllDay ? '#E85D1A' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {isAllDay && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>All day special</span>
      </div>
      {!isAllDay && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <Field label="Start time"><input className="ef-input" type="time" value={startTime} onChange={e => setStart(e.target.value)} style={{ marginTop: 4 }} /></Field>
          <Field label="End time"><input className="ef-input" type="time" value={endTime} onChange={e => setEnd(e.target.value)} style={{ marginTop: 4 }} /></Field>
        </div>
      )}
      <Field label="Deal items" hint="Add each deal separately">
        <div style={{ marginTop: 6 }}>
          {deals.map((deal, i) => (
            <DealRow key={i} deal={deal} onChange={d => updateDeal(i, d)} onRemove={() => removeDeal(i)} canRemove={deals.length > 1} />
          ))}
          <button type="button" onClick={() => setDeals(prev => [...prev, emptyDeal()])} style={{ width: '100%', padding: '9px', borderRadius: 10, border: '1.5px dashed #E0DDD8', background: 'transparent', color: '#888', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>+ Add another deal</button>
        </div>
      </Field>
      <Field label="Deal summary" hint="Short description shown on the card">
        <textarea className="ef-textarea" value={dealText} onChange={e => setDealText(e.target.value)} placeholder="e.g. $3 drafts, $2 off wells, half-off apps..." rows={2} style={{ marginTop: 4 }} />
      </Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '10px', background: '#E85D1A', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Save schedule'}
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '10px 16px', background: '#F3F4F6', color: '#555', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button type="button" onClick={handleDelete} disabled={deleting} style={{ padding: '10px 16px', background: '#fee2e2', color: '#c0392b', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {deleting ? 'Deleting...' : '🗑 Delete'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// NEW SCHEDULE FORM
// ─────────────────────────────────────────────

function NewScheduleForm({ venueId, scheduleCount, onSaved, onCancel }: {
  venueId: string; scheduleCount: number; onSaved: () => void; onCancel: () => void
}) {
  const [days, setDays]         = useState<DayOfWeek[]>([])
  const [isAllDay, setIsAllDay] = useState(false)
  const [startTime, setStart]   = useState('16:00')
  const [endTime, setEnd]       = useState('19:00')
  const [deals, setDeals]       = useState<DealItem[]>([emptyDeal()])
  const [dealText, setDealText] = useState('')
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState<string | null>(null)

  function toggleDay(day: DayOfWeek) {
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  async function handleSave() {
    if (days.length === 0) { setError('Select at least one day'); return }
    const validDeals = deals.filter(d => d.description.trim())
    setSaving(true)
    setError(null)
    try {
      const { error: err } = await supabase.from('happy_hour_schedules').insert([{
        venue_id: venueId, days, start_time: startTime, end_time: endTime,
        is_all_day: isAllDay,
        deals: validDeals.map(dealToDbShape),
        deal_text: dealText.trim() || validDeals.map(fmtDealPhrase).join(', '),
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      }])
      if (err) throw err
      onSaved()
    } catch (e: any) { setError(e.message); setSaving(false) }
  }

  return (
    <div style={{ background: '#FFF8F0', borderRadius: 12, padding: '16px', border: '2px dashed #E85D1A', marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#E85D1A', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>
        New schedule {scheduleCount + 1}
      </div>
      {error && <div style={{ background: '#fee2e2', color: '#c0392b', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
      <Field label="Days" required>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {DAYS_OF_WEEK.map(d => (
            <button key={d} type="button" onClick={() => toggleDay(d)} style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: '2px solid',
              background: days.includes(d) ? '#1A1612' : '#fff', color: days.includes(d) ? '#fff' : '#888',
              borderColor: days.includes(d) ? '#1A1612' : '#E0DDD8', transition: 'all .15s',
            }}>{d}</button>
          ))}
        </div>
      </Field>
      <div onClick={() => setIsAllDay(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 14, userSelect: 'none' }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid', borderColor: isAllDay ? '#E85D1A' : '#E0DDD8', background: isAllDay ? '#E85D1A' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {isAllDay && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>All day special</span>
      </div>
      {!isAllDay && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <Field label="Start time"><input className="ef-input" type="time" value={startTime} onChange={e => setStart(e.target.value)} style={{ marginTop: 4 }} /></Field>
          <Field label="End time"><input className="ef-input" type="time" value={endTime} onChange={e => setEnd(e.target.value)} style={{ marginTop: 4 }} /></Field>
        </div>
      )}
      <Field label="Deal items" hint="Add each deal separately">
        <div style={{ marginTop: 6 }}>
          {deals.map((deal, i) => (
            <DealRow key={i} deal={deal} onChange={d => setDeals(prev => prev.map((item, idx) => idx === i ? d : item))} onRemove={() => setDeals(prev => prev.filter((_, idx) => idx !== i))} canRemove={deals.length > 1} />
          ))}
          <button type="button" onClick={() => setDeals(prev => [...prev, emptyDeal()])} style={{ width: '100%', padding: '9px', borderRadius: 10, border: '1.5px dashed #E0DDD8', background: 'transparent', color: '#888', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>+ Add another deal</button>
        </div>
      </Field>
      <Field label="Deal summary" hint="Short description shown on the card">
        <textarea className="ef-textarea" value={dealText} onChange={e => setDealText(e.target.value)} placeholder="e.g. $3 drafts, $2 off wells, half-off apps..." rows={2} style={{ marginTop: 4 }} />
      </Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '10px', background: '#E85D1A', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Add schedule'}
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '10px 16px', background: '#F3F4F6', color: '#555', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// MAIN EDIT FORM
// ─────────────────────────────────────────────

interface EditVenueFormProps {
  venue: Venue
  onClose: () => void
  onSaved: () => void
}

export function EditVenueForm({ venue, onClose, onSaved }: EditVenueFormProps) {
  const [name, setName]                   = useState(venue.name)
  const [neighborhood, setNeighborhood]   = useState(venue.neighborhood)
  const [city, setCity]                   = useState(venue.city)
  const [address, setAddress]             = useState(venue.address ?? '')
  const [website, setWebsite]             = useState(venue.website ?? '')
  const [phone, setPhone]                 = useState(venue.phone ?? '')
  const [dogFriendly, setDogFriendly]     = useState((venue as any).dog_friendly ?? false)
  const [businessHours, setBusinessHours] = useState<any[] | null>((venue as any).business_hours ?? null)
  const [categories, setCategories]       = useState<string[]>(venue.categories ?? [])
  const [imageUrl, setImageUrl]           = useState((venue as any).image_url ?? '')
  const [coverUploading, setCoverUploading] = useState(false)
  const [coverError, setCoverError]       = useState<string | null>(null)
  const coverInputRef                     = useRef<HTMLInputElement>(null)

  const [saving, setSaving]               = useState(false)
  const [error, setError]                 = useState<string | null>(null)
  const [success, setSuccess]             = useState(false)
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null)
  const [addingSchedule, setAddingSchedule]       = useState(false)
  const [events, setEvents]                       = useState<VenueEvent[]>([])
  const [addingEvent, setAddingEvent]             = useState(false)
  const [editingEventId, setEditingEventId]       = useState<string | null>(null)
  const [eventsLoaded, setEventsLoaded]           = useState(false)

  const schedules = venue.schedules ?? []

  function handlePlaceSelect(place: PlaceResult) {
    // name intentionally not auto-updated from Google
    if (place.address)        setAddress(place.address)
    if (place.phone)          setPhone(place.phone)
    if (place.website)        setWebsite(place.website)
    if (place.neighborhood)   setNeighborhood(place.neighborhood)
    if (place.business_hours) setBusinessHours(place.business_hours)
  }

  async function handleCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setCoverError('Please select an image file.'); return }
    if (file.size > 10 * 1024 * 1024) { setCoverError('Image must be under 10MB.'); return }

    setCoverUploading(true)
    setCoverError(null)
    try {
      const ext = file.name.split('.').pop() ?? 'jpg'
      const path = `covers/${venue.id}/cover.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('venue-photos')
        .upload(path, file, { cacheControl: '3600', upsert: true })
      if (uploadErr) throw uploadErr
      const { data } = supabase.storage.from('venue-photos').getPublicUrl(path)
      setImageUrl(data.publicUrl)
    } catch (err: any) {
      setCoverError(err.message ?? 'Upload failed. Check that the venue-photos bucket exists in Supabase.')
    }
    setCoverUploading(false)
    if (coverInputRef.current) coverInputRef.current.value = ''
  }

  async function saveVenue() {
    if (!name.trim()) { setError('Venue name is required'); return }
    setSaving(true)
    setError(null)
    try {
      const { error: err } = await supabase
        .from('venues')
        .update({
          name:         name.trim(),
          neighborhood: neighborhood.trim() || 'Unknown',
          city:         city.trim() || 'Cincinnati',
          address:      address.trim() || null,
          website:      website.trim() || null,
          phone:        phone.trim() || null,
          dog_friendly:   dogFriendly,
          business_hours: businessHours,
          // Every admin save is a real re-verification moment — you
          // looked at this venue and confirmed/updated it. Without this,
          // the backfill run today would only cover venues as of right
          // now; anything you touch going forward that skips this field
          // would silently drift back to "no badge" the same way the
          // original 16 did.
          last_verified_at: new Date().toISOString(),
          categories:     categories as any,
          image_url:    imageUrl.trim() || null,
          updated_at:   new Date().toISOString(),
        })
        .eq('id', venue.id)
      if (err) throw err
      setSuccess(true)
      setTimeout(() => { setSuccess(false); onSaved() }, 1200)
    } catch (e: any) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function saveSchedule(scheduleId: string, updates: Partial<HappyHourSchedule>) {
    const { error: err } = await supabase
      .from('happy_hour_schedules')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', scheduleId)
    if (err) throw err
    setEditingScheduleId(null)
    onSaved()
  }

  async function loadEvents() {
    const data = await getVenueEvents(venue.id)
    setEvents(data)
    setEventsLoaded(true)
  }

  async function deleteSchedule(scheduleId: string) {
    const { error: err } = await supabase
      .from('happy_hour_schedules')
      .delete()
      .eq('id', scheduleId)
    if (err) throw err
    setEditingScheduleId(null)
    onSaved()
  }

  return (
    <div className="ef-form">

      {/* Header */}
      <div className="ef-header">
        <h2 className="ef-title">Edit venue</h2>
        <button className="ef-close-btn" onClick={onClose} type="button">✕</button>
      </div>

      {success && <div className="ef-success-banner">✓ Saved successfully!</div>}
      {error   && <div className="ef-error-banner">{error}</div>}

      {/* ── VENUE DETAILS ── */}
      <div className="ef-section-title">Venue details</div>

      <div className="ef-field">
        <label className="ef-label">Find on Google</label>
        <PlacesSearch onSelect={handlePlaceSelect} placeholder="Search to auto-fill details..." />
        <p style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Selecting a result overwrites the fields below</p>
      </div>

      <Field label="Bar / restaurant name" required>
        <input className="ef-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. The Eagle OTR" />
      </Field>

      <div className="ef-row">
        <Field label="Neighborhood" required>
          <input className="ef-input" value={neighborhood} onChange={e => setNeighborhood(e.target.value)} placeholder="e.g. OTR" />
        </Field>
        <Field label="City">
          <input className="ef-input" value={city} onChange={e => setCity(e.target.value)} placeholder="Cincinnati" />
        </Field>
      </div>

      <Field label="Address">
        <input className="ef-input" value={address} onChange={e => setAddress(e.target.value)} placeholder="1342 Vine St, Cincinnati, OH 45202" />
      </Field>

      <div className="ef-row">
        <Field label="Website">
          <input className="ef-input" type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
        </Field>
        <Field label="Phone">
          <input className="ef-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(513) 555-0100" />
        </Field>
      </div>

      {/* Venue attributes */}
      <div className="ef-section-title" style={{ marginTop: 4, marginBottom: 10 }}>Venue attributes</div>
      {[
        { key: 'dog_friendly_bool', label: '🐾 Dog friendly', isBool: true },
        { key: 'rooftop',           label: '🏙️ Rooftop',      isBool: false },
        { key: 'patio',             label: '☀️ Patio',        isBool: false },
        { key: 'live_music',        label: '🎵 Live music',   isBool: false },
        { key: 'sports_bar',        label: '🏈 Sports bar',   isBool: false },
        { key: 'brewery',           label: '🍺 Brewery',      isBool: false },
        { key: 'waterfront',        label: '🌊 Waterfront',    isBool: false },
      ].map(attr => {
        const isChecked = attr.isBool
          ? dogFriendly
          : categories.includes(attr.key)
        const toggle = () => {
          if (attr.isBool) {
            setDogFriendly((v: boolean) => !v)
          } else {
            setCategories(prev =>
              prev.includes(attr.key)
                ? prev.filter(c => c !== attr.key)
                : [...prev, attr.key]
            )
          }
        }
        return (
          <div
            key={attr.key}
            onClick={toggle}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12, userSelect: 'none' }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: 4, border: '2px solid',
              borderColor: isChecked ? '#E85D1A' : '#E0DDD8',
              background: isChecked ? '#E85D1A' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {isChecked && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>{attr.label}</span>
          </div>
        )
      })}

      {/* ── COVER PHOTO ── */}
      <div className="ef-section-title" style={{ marginTop: 4 }}>Cover photo</div>
      <div className="ef-field">
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>
          Shown at the top of the venue detail page. Upload or paste a URL.
        </div>

        {/* Preview */}
        {imageUrl ? (
          <div style={{ marginBottom: 10 }}>
            <img
              src={imageUrl}
              alt="Cover preview"
              style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, display: 'block', marginBottom: 6 }}
              onError={() => setCoverError('Image URL failed to load — check the URL or upload a file.')}
            />
            <button
              type="button"
              onClick={() => { setImageUrl(''); setCoverError(null) }}
              style={{ fontSize: 11, color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ✕ Remove photo
            </button>
          </div>
        ) : (
          <div style={{
            width: '100%', height: 100, borderRadius: 10, border: '2px dashed #E0DDD8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#FAFAF8', marginBottom: 10, color: '#bbb', fontSize: 13,
          }}>
            No cover photo set
          </div>
        )}

        {/* Upload button */}
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 36, padding: '0 16px', borderRadius: 8,
          background: coverUploading ? '#F0EDE8' : '#1A1612',
          color: coverUploading ? '#888' : '#fff',
          fontSize: 13, fontWeight: 600,
          cursor: coverUploading ? 'not-allowed' : 'pointer',
          marginBottom: 8,
        }}>
          {coverUploading ? '⏳ Uploading...' : '📷 Upload photo'}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleCoverUpload}
            disabled={coverUploading}
          />
        </label>

        {coverError && (
          <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{coverError}</div>
        )}

        {/* URL fallback */}
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Or paste a photo URL directly:</div>
        <input
          className="ef-input"
          value={imageUrl}
          onChange={e => { setImageUrl(e.target.value); setCoverError(null) }}
          placeholder="https://example.com/photo.jpg"
        />
      </div>

      {/* ── BUSINESS HOURS ── */}
      <div className="ef-field" style={{ marginBottom: 16 }}>
        <label className="ef-label">Business hours</label>
        {businessHours && businessHours.length > 0 ? (
          <div style={{ background: '#F8F6F1', borderRadius: 8, padding: '10px 12px', marginTop: 4 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
              Pulled from Google Places — used to prevent showing deals before the bar opens.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
              {businessHours.map((p: any, i: number) => {
                const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
                const fmt = (t: string) => {
                  const h = parseInt(t.slice(0, 2)), m = t.slice(2)
                  return `${h % 12 || 12}${m !== '00' ? ':' + m : ''}${h >= 12 ? 'pm' : 'am'}`
                }
                return (
                  <div key={i} style={{ fontSize: 12, color: '#555' }}>
                    <span style={{ fontWeight: 700, minWidth: 36, display: 'inline-block' }}>{DAYS[p.open.day]}</span>
                    {' '}{fmt(p.open.time)} – {p.close ? fmt(p.close.time) : '12am'}
                  </div>
                )
              })}
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>
              To update: use "Find on Google" above to re-select the venue.
            </div>
          </div>
        ) : (
          <div style={{ background: '#F8F6F1', borderRadius: 8, padding: '10px 12px', marginTop: 4, fontSize: 12, color: '#aaa' }}>
            No hours stored yet. Use "Find on Google" above to pull them automatically.
          </div>
        )}
      </div>

      <button className="ef-btn-save" onClick={saveVenue} disabled={saving}>
        {saving ? 'Saving...' : 'Save venue details'}
      </button>

      {/* ── SCHEDULES ── */}
      <div className="ef-divider" />
      <div className="ef-section-title">Happy hour schedules</div>

      {schedules.length === 0 && !addingSchedule && (
        <p style={{ fontSize: 13, color: '#aaa', marginBottom: 12 }}>No schedules yet — add one below.</p>
      )}

      {schedules.map((s, i) => (
        <div key={s.id}>
          {editingScheduleId === s.id ? (
            <ScheduleEditor
              schedule={s}
              index={i}
              onSave={updates => saveSchedule(s.id, updates)}
              onDelete={() => deleteSchedule(s.id)}
              onCancel={() => setEditingScheduleId(null)}
            />
          ) : (
            <div style={{
              background: '#fff', border: '1px solid #EAE6DF', borderRadius: 12,
              padding: '12px 14px', marginBottom: 8,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
                  Schedule {i + 1}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1612', marginBottom: 2 }}>
                  {s.days.length === 7 ? 'Every day' : s.days.join(', ')}
                  {' · '}
                  {s.is_all_day ? 'All day' : `${fmtTime(s.start_time)} – ${fmtTime(s.end_time)}`}
                </div>
                {s.deals && s.deals.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {s.deals.map((d: any, di: number) => {
                      const vt: DealValueType = (d.value_type === 'amount_off' || d.value_type === 'percent_off') ? d.value_type : 'price'
                      const val = vt === 'price' ? (d.price ?? d.value) : d.value
                      const suffix = val == null ? '' : vt === 'amount_off' ? ` · $${val} off` : vt === 'percent_off' ? ` · ${val}% off` : ` · $${val}`
                      return (
                        <span key={di} style={{ fontSize: 11, background: '#F8F6F1', border: '1px solid #EAE6DF', borderRadius: 6, padding: '2px 8px', color: '#555' }}>
                          {d.description}{suffix}
                        </span>
                      )
                    })}
                  </div>
                ) : s.deal_text ? (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{s.deal_text}</div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => { setEditingScheduleId(s.id); setAddingSchedule(false) }}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #EAE6DF', background: '#F8F6F1', color: '#555', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
              >
                Edit
              </button>
            </div>
          )}
        </div>
      ))}

      {addingSchedule ? (
        <NewScheduleForm
          venueId={venue.id}
          scheduleCount={schedules.length}
          onSaved={() => { setAddingSchedule(false); onSaved() }}
          onCancel={() => setAddingSchedule(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => { setAddingSchedule(true); setEditingScheduleId(null) }}
          style={{
            width: '100%', padding: '12px', borderRadius: 12,
            border: '2px dashed #E85D1A', background: 'transparent',
            color: '#E85D1A', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4,
          }}
        >
          + Add another day's schedule
        </button>
      )}

      {/* ── EVENTS ── */}
      <div className="ef-divider" />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="ef-section-title" style={{ marginBottom: 0 }}>Upcoming Events</div>
        {!eventsLoaded && (
          <button
            type="button"
            onClick={loadEvents}
            style={{ fontSize: 12, color: '#E85D1A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            Load events
          </button>
        )}
      </div>

      {eventsLoaded && (
        <>
          {events.length === 0 && !addingEvent && (
            <p style={{ fontSize: 13, color: '#aaa', marginBottom: 12 }}>No upcoming events — add one below.</p>
          )}

          {events.map(event => (
            editingEventId === event.id ? (
              <EventEditor
                key={event.id}
                event={event}
                onSave={async updates => {
                  await updateVenueEvent(event.id, updates)
                  setEditingEventId(null)
                  loadEvents()
                }}
                onDelete={async () => {
                  await deleteVenueEvent(event.id)
                  setEditingEventId(null)
                  loadEvents()
                }}
                onCancel={() => setEditingEventId(null)}
              />
            ) : (
              <div key={event.id} style={{
                background: '#fff', border: '1px solid #EAE6DF', borderRadius: 12,
                padding: '12px 14px', marginBottom: 8,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1612', marginBottom: 2 }}>
                    {event.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}{event.start_time.slice(0, 5)}
                    {' · '}{event.is_free ? 'Free' : (event.price_text ?? 'Ticketed')}
                  </div>
                  {event.description && (
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{event.description.slice(0, 60)}{event.description.length > 60 ? '...' : ''}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setEditingEventId(event.id); setAddingEvent(false) }}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #EAE6DF', background: '#F8F6F1', color: '#555', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                >
                  Edit
                </button>
              </div>
            )
          ))}

          {addingEvent ? (
            <EventEditor
              onSave={async updates => {
                await createVenueEvent({ ...updates, venue_id: venue.id } as any)
                setAddingEvent(false)
                loadEvents()
              }}
              onCancel={() => setAddingEvent(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setAddingEvent(true); setEditingEventId(null) }}
              style={{
                width: '100%', padding: '12px', borderRadius: 12,
                border: '2px dashed #22C55E', background: 'transparent',
                color: '#22C55E', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginTop: 4,
              }}
            >
              + Add event
            </button>
          )}
        </>
      )}

    </div>
  )
}

// ─────────────────────────────────────────────
// EVENT EDITOR — create or edit an event
// ─────────────────────────────────────────────

function EventEditor({
  event,
  onSave,
  onDelete,
  onCancel,
}: {
  event?: VenueEvent
  onSave: (updates: Partial<VenueEvent>) => Promise<void>
  onDelete?: () => Promise<void>
  onCancel: () => void
}) {
  const [title, setTitle]           = useState(event?.title ?? '')
  const [description, setDesc]      = useState(event?.description ?? '')
  const [eventDate, setEventDate]   = useState(event?.event_date ?? '')
  const [startTime, setStartTime]   = useState(event?.start_time ?? '19:00')
  const [endTime, setEndTime]       = useState(event?.end_time ?? '')
  const [isFree, setIsFree]         = useState(event?.is_free ?? true)
  const [priceText, setPriceText]   = useState(event?.price_text ?? '')
  const [ticketUrl, setTicketUrl]   = useState(event?.ticket_url ?? '')
  const [isFeatured, setIsFeatured] = useState(event?.is_featured ?? false)
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState<string | null>(null)

  async function handleSave() {
    if (!title.trim()) { setError('Event title is required'); return }
    if (!eventDate)    { setError('Date is required'); return }
    setSaving(true)
    setError(null)
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        event_date: eventDate,
        start_time: startTime,
        end_time: endTime || null,
        is_free: isFree,
        price_text: isFree ? null : (priceText.trim() || null),
        ticket_url: ticketUrl.trim() || null,
        is_featured: isFeatured,
        is_active: true,
      })
    } catch (e: any) { setError(e.message) }
    setSaving(false)
  }

  async function handleDelete() {
    if (!onDelete) return
    if (!window.confirm('Delete this event?')) return
    await onDelete()
  }

  return (
    <div style={{ background: '#FFF8F0', borderRadius: 12, padding: 16, border: '2px dashed #22C55E', marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#22864A', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14 }}>
        {event ? 'Edit event' : 'New event'}
      </div>
      {error && <div style={{ background: '#fee2e2', color: '#c0392b', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}

      <Field label="Event title" required>
        <input className="ef-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Trivia Night, Live Jazz, Paint & Sip" />
      </Field>
      <Field label="Description">
        <textarea className="ef-textarea" value={description} onChange={e => setDesc(e.target.value)} placeholder="What's happening? Keep it short and enticing." rows={2} />
      </Field>
      <div className="ef-row">
        <Field label="Date" required>
          <input className="ef-input" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
        </Field>
        <Field label="Start time">
          <input className="ef-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
        </Field>
        <Field label="End time">
          <input className="ef-input" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
        </Field>
      </div>

      {/* Free / Ticketed */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => setIsFree(true)}
          style={{ padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '2px solid', background: isFree ? '#DCFCE7' : '#fff', color: isFree ? '#15803D' : '#888', borderColor: isFree ? '#22C55E' : '#E0DDD8' }}
        >Free</button>
        <button
          type="button"
          onClick={() => setIsFree(false)}
          style={{ padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '2px solid', background: !isFree ? '#FEF3E2' : '#fff', color: !isFree ? '#92400E' : '#888', borderColor: !isFree ? '#F59E0B' : '#E0DDD8' }}
        >Ticketed</button>
      </div>

      {!isFree && (
        <div className="ef-row">
          <Field label="Price" hint="e.g. $10 cover, Free before 9pm">
            <input className="ef-input" value={priceText} onChange={e => setPriceText(e.target.value)} placeholder="$10 cover" />
          </Field>
          <Field label="Ticket URL">
            <input className="ef-input" type="url" value={ticketUrl} onChange={e => setTicketUrl(e.target.value)} placeholder="https://..." />
          </Field>
        </div>
      )}

      {/* Featured toggle */}
      <div onClick={() => setIsFeatured(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 14, userSelect: 'none' }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, border: '2px solid', borderColor: isFeatured ? '#E85D1A' : '#E0DDD8', background: isFeatured ? '#E85D1A' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {isFeatured && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>⭐ Feature this event</span>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={handleSave} disabled={saving} style={{ flex: 1, padding: 10, background: '#22C55E', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {saving ? 'Saving...' : event ? 'Save changes' : 'Add event'}
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '10px 16px', background: '#F3F4F6', color: '#555', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        {onDelete && (
          <button type="button" onClick={handleDelete} style={{ padding: '10px 16px', background: '#fee2e2', color: '#c0392b', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
        )}
      </div>
    </div>
  )
}
